import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  acquireUnity,
  attachUnityCanvas,
  ensureUnityHost,
  isCurrentOwner,
  markUnityUnavailable,
  releaseUnity,
  type UnityInstance,
} from '../../lib/unityViewer';
import {
  resetCameraCommand,
  seek as sendSeek,
  sendAssetToUnity,
  setPlaying,
  setSeeking,
  setTargetFps,
  setUnityEventListener,
  setViewport,
  type UnityEvent,
} from './unityBridge';
import type { VrmaBackendHandle, VrmaBackendProps } from './types';

/**
 * Unity 版でのみ必要な追加 props。types.ts の共通 VrmaBackendProps は変更しない
 * （ライト版側への影響を避けるため）ので、ここでローカルに拡張する。
 */
export interface VrmaViewerUnityProps extends VrmaBackendProps {
  /**
   * 読み込み進捗。phase は 'boot'（Unity エンジン自体の起動。createUnityInstance の
   * onProgress。0〜1 に到達後も ready まで数秒かかる）と 'download'/'parse'/'build'
   * （VRM/VRMA アセットの読み込み。Unity 側の loadProgress イベント）の両方を流す。
   */
  onLoadProgress?(ratio: number, phase: string): void;
  /** Unity の起動が完了（ready イベント受信）したとき。 */
  onReady?(): void;
}

// モバイルでは devicePixelRatio をそのまま使わず 1 に固定する（ライト版の
// setPixelRatio(Math.min(dpr,2)) と方針を揃えつつ、モバイルの GPU/メモリ負荷を抑える）。
function getDprCap(): number {
  const isMobile = navigator.maxTouchPoints > 0 || /Mobi|Android/i.test(navigator.userAgent);
  return isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
}

/**
 * Unity WebGL によるリッチ版描画バックエンド。types.ts の VrmaBackendProps/Handle を
 * three.js 版（VrmaViewerThree.tsx）と同じ契約で実装する。実際の Unity インスタンスは
 * lib/unityViewer.ts のシングルトンを acquire/attach/release するだけで、生成・破棄
 * 自体はここでは行わない。
 */
const VrmaViewerUnity = forwardRef<VrmaBackendHandle, VrmaViewerUnityProps>(function VrmaViewerUnity(
  props,
  ref,
) {
  const { vrmUrl, vrmaSource, armed } = props;

  const hostRef = useRef<HTMLDivElement>(null);
  const propsRef = useRef(props);
  propsRef.current = props;

  const tokenRef = useRef(0);
  const instanceRef = useRef<UnityInstance | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // 直近の time イベントから引いた再生位置。getElapsed() が同期的に返すために ref で保持。
  const elapsedRef = useRef(0);
  // 進行中のロード要求の reqId。イベントが古い要求のものなら無視するための目印。
  const vrmReqIdRef = useRef<string | null>(null);
  const vrmaReqIdRef = useRef<string | null>(null);

  // Unity インスタンスが attach 済みかどうか。true になったタイミングで再度
  // playing/scrubbing/rendering を同期し直すための state（ref だけだと再実行の
  // トリガーにならないため）。
  const [instanceReady, setInstanceReady] = useState(false);

  // ---- VRM/VRMA 読み込み（three.js 版 loadVrm/loadVrma と同じ順序）----
  async function loadVrma(source: string | File) {
    const instance = instanceRef.current;
    if (!instance) return;
    propsRef.current.onStatusChange('loading');
    vrmaReqIdRef.current = await sendAssetToUnity(instance, 'vrma', source, { autoPlay: true });
  }

  async function loadVrm(source: string | File) {
    const instance = instanceRef.current;
    if (!instance) return;
    propsRef.current.onStatusChange('loading');
    vrmReqIdRef.current = await sendAssetToUnity(instance, 'vrm', source, { autoPlay: true });
  }

  function handleUnityEvent(event: UnityEvent) {
    switch (event.type) {
      case 'ready':
        propsRef.current.onReady?.();
        break;
      case 'loadProgress':
        propsRef.current.onLoadProgress?.(event.payload.ratio, event.payload.phase);
        break;
      case 'vrmLoaded':
        if (event.payload.reqId !== vrmReqIdRef.current) return; // 古い要求のイベントは無視
        propsRef.current.onLoaded({ hasVrm: true, hasClip: false, durationSec: 0 });
        propsRef.current.onStatusChange('ready');
        // VRM に続けて適用する VRMA が渡り続けていれば、続けて読み込む。
        if (propsRef.current.vrmaSource) void loadVrma(propsRef.current.vrmaSource);
        break;
      case 'vrmaLoaded':
        if (event.payload.reqId !== vrmaReqIdRef.current) return;
        propsRef.current.onLoaded({
          hasVrm: true,
          hasClip: true,
          durationSec: event.payload.duration,
        });
        propsRef.current.onStatusChange('ready');
        break;
      case 'time':
        elapsedRef.current = event.payload.elapsed;
        propsRef.current.onProgress(event.payload.elapsed);
        break;
      case 'warn':
        console.warn('[unity]', event.payload.code, event.payload.message);
        break;
      case 'error': {
        const { reqId, code, message } = event.payload;
        // 特定の読み込み要求に紐付かない、かつ致命的なエラーはリッチ版そのものを無効化する。
        if (!reqId && (code === 'WEBGL_CONTEXT_LOST' || code === 'OUT_OF_MEMORY')) {
          markUnityUnavailable(message);
          propsRef.current.onUnsupported?.(message);
        }
        propsRef.current.onError(message);
        propsRef.current.onStatusChange('error');
        break;
      }
      case 'stats':
        break;
    }
  }

  // ---- マウント時に一度だけ: Unity インスタンスを確保して attach する ----
  useEffect(() => {
    let disposed = false;
    const token = acquireUnity();
    tokenRef.current = token;
    propsRef.current.onStatusChange('loading');

    // createUnityInstance の Promise が resolve するより前に Unity 側が ready を emit する
    // 可能性があるため（実際に fake ローダーで再現した）、購読は await の前に登録しておく。
    // .then 側で登録すると、resolve 直前/直後に emit された ready を取り逃す。
    setUnityEventListener(handleUnityEvent);

    ensureUnityHost((ratio) => propsRef.current.onLoadProgress?.(ratio, 'boot'))
      .then((host) => {
        if (disposed || !isCurrentOwner(token) || !hostRef.current) return;
        instanceRef.current = host.instance;
        canvasRef.current = host.canvas;
        attachUnityCanvas(token, hostRef.current);
        setInstanceReady(true);
      })
      .catch((err) => {
        if (disposed) return;
        const message = err instanceof Error ? err.message : String(err);
        propsRef.current.onUnsupported?.(message);
        propsRef.current.onStatusChange('error');
      });

    return () => {
      disposed = true;
      if (isCurrentOwner(token)) setUnityEventListener(null);
      releaseUnity(token);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- canvas のリサイズ対応 + Cmd_SetViewport ----
  useEffect(() => {
    const layer = hostRef.current;
    if (!layer) return;
    const syncViewport = () => {
      const canvas = canvasRef.current;
      const instance = instanceRef.current;
      if (!canvas || !instance) return;
      const cssWidth = layer.clientWidth;
      const cssHeight = layer.clientHeight;
      const dpr = getDprCap();
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      setViewport(instance, cssWidth, cssHeight, dpr);
    };
    const ro = new ResizeObserver(syncViewport);
    ro.observe(layer);
    syncViewport();
    return () => ro.disconnect();
  }, [instanceReady]);

  // ---- armed + vrmUrl/vrmaSource の変化で読み直す（three.js 版と同じ条件）----
  useEffect(() => {
    if (!armed || !instanceReady) return;
    void loadVrm(vrmUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, vrmUrl, vrmaSource, instanceReady]);

  // ---- playing/scrubbing/rendering の同期 ----
  useEffect(() => {
    if (instanceRef.current) setPlaying(instanceRef.current, props.playing);
  }, [props.playing, instanceReady]);

  useEffect(() => {
    if (instanceRef.current) setSeeking(instanceRef.current, props.scrubbing);
  }, [props.scrubbing, instanceReady]);

  useEffect(() => {
    // タブ非表示・画面外では描画負荷を落とす（three.js 版は rAF 自体を止めているが、
    // Unity 側は Cmd_SetTargetFps で相当の効果を得る）。
    if (instanceRef.current) setTargetFps(instanceRef.current, props.rendering ? 60 : 5);
  }, [props.rendering, instanceReady]);

  useImperativeHandle(
    ref,
    () => ({
      seek(seconds: number) {
        elapsedRef.current = seconds;
        if (instanceRef.current) sendSeek(instanceRef.current, seconds);
      },
      resetCamera() {
        if (instanceRef.current) resetCameraCommand(instanceRef.current);
      },
      getElapsed() {
        return elapsedRef.current;
      },
    }),
    [],
  );

  return <div className="viewer__layer" ref={hostRef} />;
});

export default VrmaViewerUnity;
